/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
	type CustomerModelDTO,
	type FeedbackResponse,
	type UserModel,
	FeedbacksControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import { reportPlatformError } from "$root/api/error-reporting";
import { useApiGen } from "$root/api/hooks";
import { AsyncSwitch } from "$root/components/AsyncSwitch";
import AuthorizationGuard, { hasAccess } from "$root/components/AuthorizationGuard";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { IconWalls } from "$root/components/IconWall";
import ImpersonateBox from "$root/components/ImpersonateBox/impersonate";
import { MarkdownRenderer } from "$root/components/MarkdownRenderer/MarkdownRenderer";
import { PageHeader } from "$root/components/PageHeader";
import { ReactQueriesWrapperBase, ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { accessToken$, impersonateEmail$ } from "$root/functional-areas/session/atom";
import { sphereAIAvailable } from "$root/functional-areas/sphere-ai/config";
import { downloadConversationHistory } from "$root/functional-areas/sphere-ai/historyExport";
import { UserRole, useUserValue } from "$root/functional-areas/user";
import { userRepository } from "$root/functional-areas/user/repository";
import { formatDateTime } from "$root/localization/formatters";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast } from "$root/notification-system/toast";
import { PaletteColors } from "$root/styles/themePalette";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { commonColumns } from "$root/ui-lib/common-table-columns";
import { dateYYYYMMDD, downloadContentDisposition, filterMap, useTaskQueue } from "$root/utils";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type OrderBy,
	type TableColumn,
	AutoSortHScrollTable,
	AutoTooltip,
	DatePickerRangeInput,
	Dialog,
	Icon,
	Row,
	Tab,
	TabGroup,
	TableDataCell,
	Text,
	TooltipContent,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import {
	BulkActions,
	Button,
	DatePickerInput,
	DropdownMenu,
	Form,
	FormField,
	Select,
	SubmitButton,
	TextInput,
} from "@mdotm/shadcn/mdotm/components";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSort, builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { useQuery } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { spawnCustomerDialog } from "./spawn-customer-dialog";
import { spawnUserDialog } from "./spawn-user-dialog";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import { ForEach } from "@mdotm/utils/react-extra";

type TRole = {
	[key: string]: number;
};

/**
 * What `DatePickerRangeInput` carries: no dates, a start only while one end is still
 * being chosen, or a whole range — all ISO `YYYY-MM-DD`. The partial state is why the
 * feedback export validates on the LENGTH rather than on two separate fields being
 * filled: one control owns both ends, and it reports an incomplete range as one value.
 */
type DateRange = [] | [string] | [string, string];

// User Data, View and change

const defaultFeedbackTableOrderBy: OrderBy[] = [{ columnName: "dateTime", direction: "desc" }];

const FeedbackModalBox = (props: { userId: string; userName: string }) => {
	const { userId, userName } = props;
	const [feedbackModal, setFeedbackModal] = useState(false);
	const feedbacksV2Api = useApiGen(FeedbacksControllerApiFactory);
	const { formatDateTime } = useLocaleFormatters();

	const columns = useMemo<Array<TableColumn<FeedbackResponse>>>(
		() => [
			{
				sortFn: builtInSortFnFor("dateTime"),
				name: "dateTime",
				header: "Date",
				content: ({ dateTime }) => formatDateTime(dateTime),
				width: 186,
			},
			{
				sortFn: builtInSortFnFor("referenceMessage"),
				name: "referenceMessage",
				header: "Reference Message",
				content: ({ referenceMessage }, cellProps) => (
					<AutoTooltip
						severity="info"
						overrideColor={themeCSSVars.palette_N200}
						position="right"
						disabled={!referenceMessage}
						trigger={({ innerRef }) => (
							<TableDataCell {...cellProps} title="">
								<span ref={innerRef}>{referenceMessage}</span>
							</TableDataCell>
						)}
					>
						<TooltipContent>
							<MarkdownRenderer>{referenceMessage}</MarkdownRenderer>
						</TooltipContent>
					</AutoTooltip>
				),
				minWidth: 200,
			},
			{
				name: "feedback",
				header: "Feedback message",
				content: ({ message }, cellProps) => (
					<AutoTooltip
						severity="info"
						overrideColor={themeCSSVars.palette_N200}
						position="left"
						disabled={!message}
						trigger={({ innerRef }) => (
							<TableDataCell {...cellProps} title="">
								<span ref={innerRef}>{message}</span>
							</TableDataCell>
						)}
					>
						{message}
					</AutoTooltip>
				),
				minWidth: 200,
			},
			{
				...commonColumns.integer("rating"),
				header: "rating",
				width: 96,
			},
		],
		[formatDateTime],
	);

	const { data: feedbackData } = useQueryNoRefetch({
		queryKey: ["feedback-query", userId],
		enabled: feedbackModal && Boolean(userId),
		queryFn: async () => {
			const feedback = await feedbacksV2Api.listFeedback(userId);
			return feedback.data;
		},
	});

	return (
		<>
			<Dialog
				size="xxlarge"
				show={feedbackModal}
				onClose={() => setFeedbackModal(false)}
				header={`${userName} feedbacks`}
			>
				<AutoSortHScrollTable
					columns={columns}
					orderBy={defaultFeedbackTableOrderBy}
					rows={feedbackData ?? []}
					noDataText="No Feedbacks"
					className="max-h-[400px]"
				/>
			</Dialog>
			<button
				type="button"
				onClick={() => {
					setFeedbackModal(true);
				}}
				style={{ cursor: "pointer" }}
			>
				<Icon icon="Evaluate" size={22} color={PaletteColors.GREENY_BLUE} />
			</button>
		</>
	);
};

const UsersList = (props: {
	roles: TRole;
	userRole: number;
	currentUserEmail: string;
	users: UserModel[];
	customers: CustomerModelDTO[];
	/**
	 * Open the conversation-history export for one user. Absent where the export is not
	 * on offer — a non-ROOT caller, or an environment with no sphere-mcp — which is the
	 * same condition that leaves the entry out of the Exports menu, asked once by the
	 * page and answered here by whether the callback exists.
	 */
	onExportConversations?: (userId: string) => void;
}) => {
	const { users, customers, roles, userRole, currentUserEmail, onExportConversations } = props;
	const { t } = useTranslation();

	const [filterRoles, setFilterRoles] = useState<string[]>([]);
	const [filterCustomers, setFilterCustomers] = useState<string[]>([]);

	const {
		query,
		filtered: fuzzyFiltered,
		setQuery,
	} = useSearchable({
		collection: users,
		extractFn: (row) => [row.email, row.name, row.surname].join(" "),
	});

	const filtered = useMemo(
		() =>
			fuzzyFiltered.filter(
				(row) =>
					(filterRoles.length === 0 ||
						filterRoles.some((r) => row.roles?.some((r2) => r === r2)) ||
						filterRoles.some((r) => row.role === r)) &&
					(filterCustomers.length === 0 || filterCustomers.some((c) => row.customerId === c)),
			),
		[fuzzyFiltered, filterRoles, filterCustomers],
	);
	const roleOptions = useMemo(() => t("SETTING_PANEL.ROLES_OPTIONS", { returnObjects: true }), [t]);

	const customerOptions = useMemo(
		() =>
			customers.map((c) => ({
				label: `${c.name ?? "-"} (#${c.id!})`,
				value: c.id ?? "",
			})),
		[customers],
	);

	const { queue } = useTaskQueue();

	const enableUser = React.useCallback(
		async (userData: UserModel) => {
			try {
				await queue.enqueue(() =>
					userRepository.updateUser(userData.id!, {
						...userData,
						enabled: !userData.enabled,
					}),
				);
			} catch (e) {
				console.error(e);
				platformToast({
					children: t("SETTING_PANEL.CREATE_USER_ERROR"),
					severity: "error",
					icon: "Top-menu-user",
				});
			}
		},
		[queue, t],
	);

	const { multiSelectCtx, column, rowClassName, toggle } = useSelectableTableColumn({
		filteredRows: filtered,
		rows: users,
		selectBy: (r) => r.id ?? "",
		selectableRowIds: filterMap(users, (u) =>
			u.role === UserRole.ROOT || u.roles?.includes(UserRole.ROOT) ? [] : [u.id ?? ""],
		),
	});

	const rolesOptions = t("SETTING_PANEL.ROLES_OPTIONS", { returnObjects: true });

	const columns = useMemo<Array<TableColumn<UserModel>>>(
		() => [
			column,
			{
				sortFn: builtInSortFnFor("id"),
				name: "id",
				header: "ID",
				cellStyle: ({ email }) => ({
					color: email === currentUserEmail ? themeCSSVars.global_palette_primary_600 : undefined,
					textDecorationColor: email === currentUserEmail ? themeCSSVars.global_palette_primary_600 : undefined,
					textDecoration: email === currentUserEmail ? "underline" : undefined,
				}),
				content: ({ id }) => id,
				width: 216,
			},
			{
				sortFn: builtInSortFnFor("email"),
				name: "email",
				header: "Email",
				cellStyle: ({ email }) => ({
					color: email === currentUserEmail ? themeCSSVars.global_palette_primary_600 : undefined,
				}),
				content: ({ email }) => email ?? "-",
				width: 216,
			},
			{
				sortFn: (a, b) => builtInSort(a.name?.toLowerCase(), b.name?.toLowerCase()),
				header: "Name",
				name: "Name",
				cellStyle: ({ email }) => ({
					color: email === currentUserEmail ? themeCSSVars.global_palette_primary_600 : undefined,
				}),
				content: ({ name }) => name ?? "-",
				minWidth: 144,
			},
			{
				sortFn: (a, b) => builtInSort(a.surname?.toLowerCase(), b.surname?.toLowerCase()),
				header: "Surname",
				name: "surname",
				cellStyle: ({ email }) => ({
					color: email === currentUserEmail ? themeCSSVars.global_palette_primary_600 : undefined,
				}),
				content: ({ surname }) => surname ?? "-",
				minWidth: 144,
			},
			{
				sortFn: builtInSortFnFor("role"),
				name: "role",
				header: "Role",
				cellStyle: ({ email }) => ({
					color: email === currentUserEmail ? themeCSSVars.global_palette_primary_600 : undefined,
				}),
				content: ({ role }) => rolesOptions.find((r) => r.value === role)?.label ?? role ?? "-",
				width: 96,
			},

			{
				sortFn: builtInSortFnFor("creation_time"),
				name: "creation_time",
				header: "Creation Time",
				cellStyle: ({ email }) => ({
					color: email === currentUserEmail ? themeCSSVars.global_palette_primary_600 : undefined,
				}),
				content: ({ creation_time }) => creation_time?.substring(0, 10) ?? "-",
				width: 96,
			},
			{
				sortFn: builtInSortFnFor("customerId"),
				name: "customerId",
				header: "Customer",
				cellStyle: ({ email }) => ({
					color: email === currentUserEmail ? themeCSSVars.global_palette_primary_600 : undefined,
				}),
				content: ({ customerId }) => customers.find((c) => c.id === customerId)?.name ?? "-",
				width: 144,
			},
			{
				name: "services",
				header: "Services",
				content: ({ services }, cellProps) => {
					return (
						<AutoTooltip
							severity="info"
							overrideColor={themeCSSVars.palette_N200}
							trigger={({ innerRef }) => (
								<Row innerRef={innerRef} gap={8} alignItems="center" {...cellProps}>
									<p className="font-semibold">Enabled</p>
									<div
										className={`flex justify-center items-center w-5 h-5 rounded-full bg-[color:var(--palette-P400)] text-white font-sm`}
									>
										{services?.length}
									</div>
								</Row>
							)}
						>
							<TooltipContent>
								<ForEach collection={services ?? []}>{(service) => <p>{t(`SERVICES.${service}`)}</p>}</ForEach>
							</TooltipContent>
						</AutoTooltip>
					);
				},
				width: 144,
			},
			{
				sortFn: builtInSortFnFor("enabled"),
				name: "enabled",
				header: "Enabled",
				content: (row, cellProps) => {
					const { enabled, email, role } = row;
					const isUserDisabled = userRole <= roles[role!];
					const isCurrentUser = currentUserEmail === email;
					return isCurrentUser ? null : (
						<TableDataCell {...cellProps}>
							<AsyncSwitch
								onClick={(e) => e.stopPropagation()}
								disabled={isUserDisabled}
								checked={enabled ?? false}
								onChange={() => enableUser(row)}
							/>
						</TableDataCell>
					);
				},
				width: 144,
			},
			{
				name: "actions",
				header: "Actions",
				// Fits the four icons a ROOT caller sees on another user's row — profile,
				// impersonate, feedback, export — at the gap the Row below sets.
				width: 160,
				content: (row, cellProps) => {
					const { email, role, id } = row;
					const isUserDisabled = userRole <= roles[role!];
					const isCurrentUser = currentUserEmail === email;
					return (
						<Row
							gap={16}
							onClick={(e) => e.stopPropagation()}
							alignItems="center"
							justifyContent="start"
							{...cellProps}
						>
							{!isCurrentUser ? (
								<>
									<button
										type="button"
										className="border-transparent bg-transparent"
										style={{ cursor: isUserDisabled ? undefined : "pointer" }}
										disabled={isUserDisabled}
										onClick={() => {
											spawnUserDialog({ userData: row, customers, isCurrentUser });
										}}
									>
										<ManageAccountsIcon disabled={isUserDisabled} />
									</button>
									<AuthorizationGuard requiredRole="ROOT">
										{() => (
											<>
												<ImpersonateBox mode="Button" email={email ?? ""} />
												<FeedbackModalBox userId={id ?? ""} userName={email ?? ""} />
												<ExportConversationsButton userId={id} email={email} onExport={onExportConversations} />
											</>
										)}
									</AuthorizationGuard>
								</>
							) : (
								<AuthorizationGuard requiredRole="ROOT">
									{() => (
										<>
											<FeedbackModalBox userId={id ?? ""} userName={email ?? ""} />
											<ExportConversationsButton userId={id} email={email} onExport={onExportConversations} />
										</>
									)}
								</AuthorizationGuard>
							)}
						</Row>
					);
				},
			},
		],
		[column, currentUserEmail, rolesOptions, customers, t, userRole, roles, enableUser, onExportConversations],
	);
	return (
		<>
			{!users ? (
				<IconWalls.ErrorData />
			) : (
				<>
					<div className="mb-4 flex gap-4">
						<FormField label={<>&nbsp;</>}>
							<TextInput
								leftContent={<Icon icon="Search" />}
								value={query}
								onChangeText={setQuery}
								placeholder="Search user info"
								className="max-w-[384px]"
							/>
						</FormField>
						<FormField label="Filter by role:">
							<Select
								enableSearch
								className="flex-1"
								options={roleOptions}
								value={filterRoles}
								multi
								placeholder="Roles"
								onChange={setFilterRoles}
								disabled={roleOptions.length === 0}
							/>
						</FormField>
						<FormField label="Filter by customer:">
							<Select
								className="flex-1"
								options={customerOptions}
								value={filterCustomers}
								enableSearch
								multi
								placeholder="Customers"
								onChange={setFilterCustomers}
								disabled={customerOptions.length === 0}
							/>
						</FormField>
					</div>
					<BulkActions
						variant="floating"
						onClear={() => multiSelectCtx.reset()}
						count={multiSelectCtx.selection.size}
						actions={[
							{
								label: "Edit services",
								onClick: () => {
									spawnUserDialog({
										userData: users.filter((u) => multiSelectCtx.selection.has(u.id ?? "")),
										customers,
									});
								},
							},
						]}
					/>
					<AutoSortHScrollTable
						orderBy={defaultUsersTableOrderBy}
						columns={columns}
						onRowClick={(row) => toggle(row.id ?? "")}
						rows={filtered}
						className="max-h-[calc(100dvh-234px)]"
						noDataText={t("SETTING_PANEL.USERS_EMPTY")}
						rowClassName={rowClassName}
						rowKey={(x) => x.id ?? ""}
					/>
				</>
			)}
		</>
	);
};

const defaultUsersTableOrderBy: OrderBy[] = [{ columnName: "email", direction: "asc" }];

const CustomersList = ({ customers }: { customers: CustomerModelDTO[] }) => {
	const { t } = useTranslation();

	const { query, filtered, setQuery } = useSearchable({
		collection: customers,
		extractFn: ({ name }) => name ?? "",
	});

	const { queue } = useTaskQueue();

	const changeCustomerStatus = React.useCallback(
		async (c: CustomerModelDTO): Promise<void> => {
			try {
				await queue.enqueue(() => userRepository.updateCustomer(c));
			} catch (error) {
				reportPlatformError(error, "ERROR", "advanced-settings", {
					message: `to update customer "${c.id}"`,
					payload: JSON.stringify(c),
				});
				platformToast({
					children: t("SETTING_PANEL.CREATE_CUSTOMER_ERROR"),
					severity: "error",
					icon: "Top-menu-user",
				});
			}
		},
		[queue, t],
	);

	const columns = useMemo<Array<TableColumn<CustomerModelDTO>>>(
		() => [
			{
				sortFn: builtInSortFnFor("id"),
				name: "id",
				header: "ID",
				content: (row) => row.id,
				width: 216,
			},
			{
				sortFn: (a, b) => builtInSort(a.name?.toLowerCase(), b.name?.toLowerCase()),
				name: "name",
				header: "Name",
				content: (row) => row.name ?? "-",
				minWidth: 244,
			},
			{
				...commonColumns.integer("maxUsers"),
				header: "Max Users",
				width: 144,
			},
			{
				...commonColumns.integer("maxPortfolios"),
				header: "Max Portfolios",
				width: 144,
			},
			{
				sortFn: builtInSortFnFor("enabled"),
				name: "enabled",
				header: "Enabled",
				width: 96,
				content: (row, cellProps) => {
					return (
						<Row justifyContent="center" {...cellProps} alignItems="center">
							<AsyncSwitch
								checked={row.enabled ?? false}
								onChange={(enabled: boolean) =>
									changeCustomerStatus({
										...row,
										enabled,
									})
								}
							/>
						</Row>
					);
				},
			},
			{
				name: "actions",
				header: "Actions",
				width: 64,
				content: (row, cellProps) => {
					return (
						<Row justifyContent="center" {...cellProps} alignItems="center">
							<button
								type="button"
								data-qualifier="settingsPanel/customer/edit"
								onClick={() => spawnCustomerDialog({ customerData: row })}
							>
								<ManageAccountsIcon />
							</button>
						</Row>
					);
				},
			},
		],
		[changeCustomerStatus],
	);

	return (
		<>
			{!customers ? (
				<IconWalls.ErrorData />
			) : (
				<>
					<div className="mb-4">
						<TextInput
							leftContent={<Icon icon="Search" />}
							value={query}
							onChangeText={setQuery}
							placeholder="Search customer info"
							className="max-w-[384px]"
						/>
					</div>
					<AutoSortHScrollTable
						orderBy={defaultCustomerListOrderBy}
						columns={columns}
						rows={filtered}
						noDataText={t("SETTING_PANEL.CUSTOMERS_EMPTY")}
						rowKey={(x) => x.id ?? ""}
						className="max-h-[calc(100dvh-234px)]"
					/>
				</>
			)}
		</>
	);
};

const defaultCustomerListOrderBy: OrderBy[] = [{ columnName: "id", direction: "asc" }];

function UserSettingsPanel(): React.JSX.Element {
	const { t } = useTranslation();
	const userData = useUserValue();
	const accessToken = useAtomValue(accessToken$);
	const impersonateEmail = useAtomValue(impersonateEmail$);

	const feedbacksV2Api = useApiGen(FeedbacksControllerApiFactory);
	const [tabIndex, setTabIndex] = useState(0);
	const [modalExport, setModalExport] = useState(false);
	const [modalConversationExport, setModalConversationExport] = useState(false);

	const ROLES: TRole = {
		GUEST: 1,
		MANAGER: 2,
		ADMIN: 3,
		ROOT: 4,
	};

	const validRole = useMemo<UserRole>(() => userData.roles?.[0] ?? "GUEST", [userData]);

	const userRole = ROLES[validRole.replace(/[[\]']+/g, "")];

	const customerListQuery = userRepository.customerList.useQuery();
	const userListQuery = userRepository.userList.useQuery();
	// The "user" card (profile dialog, feedback export) must resolve the identity the session is
	// *acting as*: `info` only carries the impersonator's bearer token, so while impersonating it
	// answers with the impersonator's own profile — the dialog would then show and update the wrong
	// user. `infoImpersonated` adds `X-Impersonate-Email`, mirroring `authSessionInit`.
	const infoQuery = useQuery(
		accessToken && impersonateEmail
			? userRepository.infoImpersonated.getQueryConfig(accessToken, impersonateEmail)
			: userRepository.info.getQueryConfig(accessToken),
	);

	const { control, formState, handleSubmit, reset } = useForm({
		defaultValues: { dateRange: [] as DateRange },
		resolver: zodResolver(
			z.object({
				// The export needs both ends, and the picker reports a half-chosen range as a
				// one-element value — so the requirement is a length, checked on the one field
				// the control owns.
				dateRange: z
					.union([z.tuple([]), z.tuple([z.string()]), z.tuple([z.string(), z.string()])])
					.refine((range) => range.length === 2, t("REQUIRED_FIELD")),
			}),
		),
	});

	const handleFeedbackDownload = React.useCallback(
		async ({ fromDate, toDate, userId }: { fromDate: string; toDate: string; userId?: string }) => {
			try {
				const formattedDates = {
					fromDate: formatDateTime(new Date(fromDate)),
					toDate: formatDateTime(new Date(toDate)),
				};
				const response = await feedbacksV2Api.exportFeedbacks(formattedDates.fromDate, formattedDates.toDate, userId, {
					responseType: "blob",
				});

				downloadContentDisposition(response);
			} catch (error) {
				platformToast({
					children: "Failed to download",
					severity: "error",
					icon: "Dowload",
				});
				console.error(error);
			} finally {
				setModalExport(false);
				reset();
			}
		},
		[feedbacksV2Api, reset],
	);

	/**
	 * The conversation-history export's own form — an optional date and an optional set
	 * of users, so no resolver and no validation: an empty field is the "all of them"
	 * case, not an incomplete one. The feedback export above requires both of its dates,
	 * which is why the two do not share a form.
	 */
	const conversationExportForm = useForm({
		defaultValues: { since: null as string | null, ownerIds: [] as string[] },
	});

	const handleConversationHistoryDownload = React.useCallback(
		async ({ since, ownerIds }: { since: string | null; ownerIds: string[] }) => {
			try {
				await downloadConversationHistory({
					since: since || undefined,
					ownerIds: ownerIds.length > 0 ? ownerIds : undefined,
				});
			} catch (error) {
				reportPlatformError(error, "ERROR", "advanced-settings", {
					message: "to export the Sphere AI conversation history",
				});
				platformToast({
					children: "Failed to download",
					severity: "error",
					icon: "Dowload",
				});
			} finally {
				setModalConversationExport(false);
				conversationExportForm.reset();
			}
		},
		[conversationExportForm],
	);

	/**
	 * Open the export dialog, either for everyone (the Exports menu) or preselected to
	 * one user (a row action). `reset` rather than `setValue` so the dialog never opens
	 * carrying what the previous one was asked for.
	 */
	const openConversationExport = React.useCallback(
		(userId?: string) => {
			conversationExportForm.reset({ since: null, ownerIds: userId ? [userId] : [] });
			setModalConversationExport(true);
		},
		[conversationExportForm],
	);

	/**
	 * Whether to offer the conversation-history export at all. Two switches, because
	 * they answer different questions: the ROOT role is what sphere-mcp's own gate
	 * checks (its history-export.ts), asserted here so the entry is not offered to a
	 * user the server would refuse — and `sphereAIAvailable`, because an environment
	 * with no sphere-mcp deployed has no conversation store to export and no address to
	 * ask (../../functional-areas/sphere-ai/config.ts).
	 */
	const canExportConversationHistory = hasAccess(userData, { requiredRole: UserRole.ROOT }) && sphereAIAvailable();

	/**
	 * Who the picker offers. Off `userListQuery` — the same list the table below renders,
	 * already loaded for it — labelled by email, because that is what an operator knows a
	 * user by and what a row action is read from. The value is the Sphere user id, which
	 * is what the export filters on.
	 */
	const conversationExportUserOptions = useMemo(
		() =>
			(userListQuery.data ?? []).map((u) => ({
				label: u.email ?? u.id ?? "-",
				value: u.id ?? "",
			})),
		[userListQuery.data],
	);

	const crumbs = useCrumbs();

	return (
		<>
			<PageHeader crumbs={crumbs.adminPanel("Global settings panel")} />

			{/* Mounted outside the query wrappers below: unlike the feedback export, this one needs no
			    `info` and no customer list. Its user picker reads `userListQuery` directly, so a list
			    still loading leaves the picker empty and the export means "every user" — which is what
			    an operator who picked nobody asked for anyway. */}
			<Dialog
				size="large"
				show={modalConversationExport}
				onClose={() => setModalConversationExport(false)}
				header="Conversation History Export"
			>
				<Form
					noValidate
					onSubmitAsync={() =>
						conversationExportForm.handleSubmit((x) =>
							handleConversationHistoryDownload({ since: x.since, ownerIds: x.ownerIds }),
						)()
					}
					className="space-y-4 w-full"
				>
					<div className="grid grid-cols-2 gap-4">
						<FormField label="Users" className="w-full">
							{({ id }) => (
								<FormController
									name="ownerIds"
									control={conversationExportForm.control}
									render={({ field: { onChange, value } }) => (
										<Select
											id={id}
											className="flex-1"
											options={conversationExportUserOptions}
											value={value}
											multi
											enableSearch
											placeholder="All users"
											onChange={onChange}
											disabled={conversationExportUserOptions.length === 0}
										/>
									)}
								/>
							)}
						</FormField>
						<FormField label="Active since" className="w-full">
							{({ id, invalid }) => (
								<FormController
									name="since"
									control={conversationExportForm.control}
									render={({ field: { onChange, value, ref } }) => (
										<DatePickerInput
											id={id}
											innerRef={ref}
											invalid={invalid}
											enableClear
											// A conversation cannot have been written to after today, so a later
											// date selects nothing.
											max={dateYYYYMMDD(new Date())}
											value={value}
											onChange={onChange}
										/>
									)}
								/>
							)}
						</FormField>
					</div>
					<div className="flex justify-end">
						<SubmitButton>Download</SubmitButton>
					</div>
				</Form>
			</Dialog>

			<AuthorizationGuard requiredRole="ROOT">
				{() => (
					<ReactQueryWrapperBase query={userListQuery}>
						{(userList) => <ImpersonateBox mode="Box" users={userList} />}
					</ReactQueryWrapperBase>
				)}
			</AuthorizationGuard>
			<ReactQueriesWrapperBase queries={[infoQuery, customerListQuery]} loadingFallback={<></>}>
				{([info, customers]) => (
					<>
						{/* Modals */}

						<Dialog size="medium" show={modalExport} onClose={() => setModalExport(false)} header="Feedback Export">
							<Form
								noValidate
								onSubmitAsync={() =>
									handleSubmit(({ dateRange: [fromDate, toDate] }) => {
										// A narrowing, not a check: the schema admits no shorter range, so the
										// submit this runs under cannot carry one.
										if (!fromDate || !toDate) {
											return;
										}
										return handleFeedbackDownload({ fromDate, toDate, userId: info.id });
									})()
								}
								className="space-y-4"
							>
								<FormField label="Period">
									{({ id, invalid }) => (
										<FormController
											name="dateRange"
											control={control}
											render={({ field: { onChange, value, ref } }) => (
												<DatePickerRangeInput
													innerRef={ref}
													invalid={invalid}
													// Nothing was fed back before this export ran, so a range reaching
													// into the future can only be a typo.
													max={dateYYYYMMDD(new Date())}
													value={value}
													onChange={onChange}
													startInputProps={{ id }}
												/>
											)}
										/>
									)}
								</FormField>
								<div className="flex justify-end">
									<SubmitButton disabled={!formState.isValid}>Download</SubmitButton>
								</div>
							</Form>
						</Dialog>
						{/* Modals */}
						<div className="grid grid-cols-3 gap-4">
							<div
								className="flex flex-col justify-between"
								style={{
									borderLeft: "8px solid #4cb09c",
									background: "white",
									padding: 16,
									marginBottom: 16,
									borderRadius: "0 6px 6px 0",
								}}
							>
								<div>
									<div className="flex items-center mb-2">
										<Icon icon="Top-menu-user" size={30} />
										&nbsp;
										<Text type="Body/XL/Bold">{t("SETTING_PANEL.USER")}</Text>
									</div>

									<Text type="Title/S" style={{ display: "block", marginBottom: "24px", fontSize: "24px" }}>
										Welcome {userData.email}
									</Text>
								</div>

								<div>
									<Button
										palette="primary"
										onClick={() => {
											if (customerListQuery.data) {
												spawnUserDialog({
													userData: info,
													customers,
													isCurrentUser: userData.email === info.email,
												});
											}
										}}
									>
										{t("SETTING_PANEL.UPDATE_USER")}
									</Button>
								</div>
							</div>
							<div
								className="flex flex-col justify-between"
								style={{
									borderLeft: "8px solid #4cb09c",
									background: "white",
									padding: 16,
									marginBottom: 16,
									borderRadius: "0 6px 6px 0",
								}}
							>
								<div>
									<div className="flex items-center mb-2">
										<div className="relative w-[20px] h-[20px]">
											<Icon icon="Top-menu-user" size={12} className="absolute -left-1 top-0" />
											<Icon icon="Top-menu-user" size={12} className="absolute left-1 top-2" />
										</div>
										&nbsp;
										<Text type="Body/XL/Bold">{t("SETTING_PANEL.USERS")}</Text>
									</div>

									<Text type="Title/S" style={{ display: "block", marginBottom: "24px", fontSize: "30px" }}>
										{userListQuery.data?.length ?? "..."}
									</Text>
								</div>

								<div>
									<Button palette="primary" onClick={() => spawnUserDialog({ customers })}>
										{t("SETTING_PANEL.NEW_USER")}
									</Button>
								</div>
							</div>
							<div
								className="flex flex-col justify-between"
								style={{
									borderLeft: "8px solid #4cb09c",
									background: "white",
									padding: 16,
									marginBottom: 16,
									borderRadius: "0 6px 6px 0",
								}}
							>
								<div>
									<div className="flex items-center mb-2">
										<div className="relative w-[20px] h-[20px]">
											<Icon icon="Top-menu-user" size={12} className="absolute left-0 top-0" />
											<Icon icon="Top-menu-user" size={12} className="absolute left-1 top-2" />
											<Icon icon="Top-menu-user" size={12} className="absolute -left-1 top-2" />
										</div>
										&nbsp;
										<Text type="Body/XL/Bold">{t("SETTING_PANEL.CUSTOMERS")}</Text>
									</div>

									<Text type="Title/S" style={{ display: "block", marginBottom: "24px", fontSize: "30px" }}>
										{customerListQuery.data?.length ?? "..."}
									</Text>
								</div>

								<div>
									<Button
										palette="primary"
										data-qualifier="settingsPanel/customer/new"
										onClick={() => spawnCustomerDialog()}
									>
										{t("SETTING_PANEL.NEW_CUSTOMER")}
									</Button>
								</div>
							</div>
						</div>
					</>
				)}
			</ReactQueriesWrapperBase>

			<ReactQueriesWrapperBase queries={[userListQuery, customerListQuery]}>
				{([userList, customerList]) => (
					<div
						style={{
							position: "relative",
							borderLeft: "8px solid #4cb09c",
							background: "white",
							padding: 16,
							marginBottom: 16,
							borderRadius: "0 6px 6px 0",
						}}
					>
						<TabGroup palette="primary" tabIndex={tabIndex} onTabChange={setTabIndex}>
							<Tab title="User List">
								<UsersList
									roles={ROLES}
									userRole={userRole}
									users={userList ?? []}
									customers={customerList ?? []}
									currentUserEmail={userData.email}
									onExportConversations={canExportConversationHistory ? openConversationExport : undefined}
								/>
							</Tab>
							<Tab title="Customer List">
								<CustomersList customers={customerList ?? []} />
							</Tab>
						</TabGroup>
						<div className="absolute right-4 top-4">
							<DropdownMenu
								Trigger={WidgetDownloadTrigger}
								actions={[
									[
										"Exports",
										[
											{
												icon: "Dowload",
												onClick: () => {
													setModalExport(true);
												},
												label: "Download Feedbacks",
											},
											...(canExportConversationHistory
												? [
														{
															icon: "Dowload" as const,
															onClick: () => {
																openConversationExport();
															},
															label: "Download Conversation History",
														},
													]
												: []),
										],
									],
								]}
							/>
						</div>
					</div>
				)}
			</ReactQueriesWrapperBase>
		</>
	);
}

export default UserSettingsPanel;

/**
 * The user table's "export this user's conversations" action. Renders nothing when the
 * page is not offering the export at all, so the column is unchanged for a caller who
 * would only be refused — see `UsersList`'s `onExportConversations`.
 *
 * It opens the same dialog the Exports menu does, with this user preselected, rather
 * than downloading on the spot: the date window is worth offering here too, and one
 * dialog is one place where what the file will contain is stated.
 */
function ExportConversationsButton(props: {
	userId?: string;
	email?: string;
	onExport?: (userId: string) => void;
}): React.JSX.Element | null {
	const { userId, email, onExport } = props;
	if (!onExport || !userId) {
		return null;
	}
	return (
		<button
			type="button"
			className="border-transparent bg-transparent"
			style={{ cursor: "pointer" }}
			title={`Export ${email ?? "this user"}'s conversation history`}
			aria-label={`Export ${email ?? "this user"}'s conversation history`}
			onClick={() => onExport(userId)}
		>
			<Icon icon="Dowload" size={22} color={PaletteColors.GREENY_BLUE} />
		</button>
	);
}

function ManageAccountsIcon({ disabled }: { disabled?: boolean }) {
	return (
		<div className="relative z-0 inline-block">
			<Icon
				size={24}
				icon="Top-menu-user"
				className="relative z-0"
				color={disabled ? PaletteColors.LIGHT_GREY : PaletteColors.GREENY_BLUE}
			/>
			<Icon
				size={11}
				icon="Settings"
				className="absolute z-10 bottom-0 right-0 bg-white rounded-full"
				color={disabled ? PaletteColors.LIGHT_GREY : PaletteColors.GREENY_BLUE}
			/>
		</div>
	);
}
